import clsx from "clsx";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

/** Cartão: a superfície de tudo. Borda fina, canto 16px, sombra quase nula. */
export function Card({ title, subtitle, action, children, className, semPadding }: {
  title?: string; subtitle?: string; action?: ReactNode;
  children: ReactNode; className?: string; semPadding?: boolean;
}) {
  return (
    <section className={clsx("cartao shadow-[var(--shadow-xs)]", className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-4 px-5 pt-5 md:px-6 md:pt-6">
          <div>
            {title && (
              <h2 className="text-base font-semibold text-ink-800 dark:text-white/90">{title}</h2>
            )}
            {subtitle && <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={semPadding ? "" : "p-5 md:p-6"}>{children}</div>
    </section>
  );
}

export function Button({ variant = "primary", className, ...p }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "ghost" | "outline" | "perigo";
  }) {
  return (
    <button {...p} className={clsx(
      "inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium t-fast",
      "disabled:cursor-not-allowed disabled:opacity-50",
      variant === "primary" &&
        "bg-[var(--color-brand-tenant)] text-[var(--color-on-brand)] shadow-[var(--shadow-xs)] hover:brightness-110",
      variant === "outline" &&
        "border border-ink-200 bg-white text-ink-700 shadow-[var(--shadow-xs)] hover:bg-ink-50 dark:border-ink-800 dark:bg-white/[0.03] dark:text-ink-200 dark:hover:bg-white/[0.06]",
      variant === "ghost" &&
        "text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5",
      variant === "perigo" &&
        "bg-[var(--color-critical)] text-white shadow-[var(--shadow-xs)] hover:brightness-110",
      className)} />
  );
}

/** Selo de estado. Cor sempre acompanhada de texto. */
export function Selo({ tom = "neutro", children, className }: {
  tom?: "neutro" | "bom" | "atencao" | "grave" | "critico" | "marca";
  children: ReactNode; className?: string;
}) {
  const cores: Record<string, string> = {
    neutro: "bg-ink-100 text-ink-700 dark:bg-white/5 dark:text-ink-300",
    bom: "bg-[var(--color-good)]/10 text-[var(--color-good)]",
    atencao: "bg-[var(--color-warning)]/12 text-[#b54708]",
    grave: "bg-[var(--color-serious)]/12 text-[#b93815]",
    critico: "bg-[var(--color-critical)]/10 text-[var(--color-critical)]",
    marca: "bg-[var(--color-brand-tenant)]/10 text-[var(--color-brand-text)]",
  };
  return (
    <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-1",
                          "text-xs font-medium", cores[tom], className)}>
      {children}
    </span>
  );
}

/**
 * Avatar de cadastro. Sem foto, mostra as iniciais — um quadrado vazio na
 * lista parece imagem quebrada, e "quebrado" é o que o operador reporta.
 *
 * Mora aqui porque a lista de cadastros e a lista do mapa mostram o MESMO
 * veículo: duas cópias divergiriam no dia em que uma ganhasse um detalhe.
 */
export function Avatar({ url, nome, quadrado, className }: {
  url: string | null; nome: string; quadrado?: boolean; className?: string;
}) {
  const iniciais = nome.trim().split(/\s+/).slice(0, 2).map(p => p[0] ?? "").join("").toUpperCase();
  return (
    <span className={clsx("flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden",
                          "bg-ink-100 text-[10px] font-semibold text-ink-500 dark:bg-ink-800",
                          quadrado ? "rounded-md" : "rounded-full", className)}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : iniciais}
    </span>
  );
}

/** Ponto de status: cor + rótulo, nunca cor sozinha. */
export function StatusDot({ status, speed, hiddenLabel, hiddenStatus }: { status: string; speed: number; hiddenLabel?: boolean; hiddenStatus?: boolean }) {
  const [cor, label] =
    status !== "online" ? ["#98a2b3", "offline"]
    : speed > 1 ? ["var(--color-moving)", "em movimento"]
    : status === "online" && speed === 0 ? ["var(--color-good)", "online"] : ["var(--color-warning)", "parado"];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={label}>
      {hiddenStatus ? null : <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: cor }} aria-hidden />}
      {hiddenLabel ? null : <span className="text-ink-500 dark:text-ink-400">{label}</span>}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 py-12 text-sm text-ink-500">
      <motion.span
        className="h-4 w-4 rounded-full border-2 border-ink-300 border-t-transparent"
        animate={{ rotate: 360 }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} />
      {label}
    </div>
  );
}

/**
 * Paginação.
 *
 * O plano vende dispositivos ilimitados, então qualquer lista pode chegar a
 * milhares de linhas. Sem isto, a página de cadastros de 300 veículos já saía
 * com 11 mil pixels de altura.
 */
export function Paginacao({ pagina, paginas, total, porPagina, aoIr }: {
  pagina: number; paginas: number; total: number; porPagina: number;
  aoIr: (p: number) => void;
}) {
  if (paginas <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-ink-200 px-5 py-4
                    dark:border-ink-800">
      <span className="tnum text-sm text-ink-500 dark:text-ink-400">
        {pagina * porPagina + 1}–{Math.min((pagina + 1) * porPagina, total)} de {total}
      </span>
      <div className="flex items-center gap-1.5">
        <Pag onClick={() => aoIr(pagina - 1)} disabled={pagina === 0} aria-label="Página anterior">
          <ChevronLeft size={15} />
        </Pag>
        {Array.from({ length: paginas }, (_, i) => i)
          .filter(i => i === 0 || i === paginas - 1 || Math.abs(i - pagina) <= 1)
          .map((i, idx, arr) => (
            <Fragment key={i}>
              {idx > 0 && arr[idx - 1] !== i - 1 && <span className="px-1 text-ink-400">…</span>}
              <Pag onClick={() => aoIr(i)} ativo={i === pagina}>{i + 1}</Pag>
            </Fragment>
          ))}
        <Pag onClick={() => aoIr(pagina + 1)} disabled={pagina >= paginas - 1}
             aria-label="Próxima página">
          <ChevronRight size={15} />
        </Pag>
      </div>
    </div>
  );
}

function Pag({ children, ativo, ...p }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { ativo?: boolean }) {
  return (
    <button {...p} aria-current={ativo ? "page" : undefined}
      className={clsx("tnum flex h-9 min-w-9 items-center justify-center rounded-lg px-2",
        "text-sm font-medium t-fast disabled:cursor-not-allowed disabled:opacity-40",
        ativo ? "bg-[var(--color-brand-tenant)] text-[var(--color-on-brand)]"
              : "text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5")}>
      {children}
    </button>
  );
}

/** Cabeçalho de tabela no padrão do TailAdmin: caixa alta, fina, discreta. */
export function Th({ children, right, center, className, ...p }:
  React.ThHTMLAttributes<HTMLTableCellElement> & { right?: boolean; center?: boolean }) {
  return (
    <th {...p} className={clsx(
      "px-5 py-3 text-xs font-medium uppercase tracking-wide text-ink-500",
      right ? "text-right" : center ? "text-center" : "text-left", className)}>
      {children}
    </th>
  );
}
